import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
import { IABCategory, Language } from '@/common/models';
import GeofenceGroup from '@/common/models/geo/GeofenceGroup';
import Location from '@/common/models/geo/Location';
import Market from '@/common/models/geo/Market';
import MediaType from '@/common/models/misc-api-models/MediaType';
import Network from '@/common/models/misc-api-models/Network';
import Site from '@/common/models/site/Site';
import Unit from '@/common/models/unit/Unit';
import GeofenceSiteModel from '@/org-app/components/geofenceGroups/GeofenceSiteModel';
import GeofenceUnitModel from '@/org-app/components/geofenceGroups/GeofenceUnitModel';

export type BudgetBreakdownTargetedIDs = Array<{ id: number; name: string }[]>;

export interface ILocationTargetingData {
  excludedCreativeOrientations: CreativeOrientationType[];
  excludedCreativeFormats: string[];
  excludedIabCategories: IABCategory[];
  excludedLanguages: Language[];
  excludedLocations: Location[];
  excludedSites: Site[];
  excludedUnits: Unit[];
  excludedMarkets: Market[];
  excludedMediaTypes: MediaType[];
  excludedNetworks: Network[];
  excludedGeofenceGroups: GeofenceGroup[];
  geofenceGroups: GeofenceGroup[];
  hasAccessToTargetedGeofenceGroups: boolean;
  creativeOrientations: CreativeOrientationType[];
  creativeFormats: string[];
  iabCategories: IABCategory[];
  languages: Language[];
  locations: Location[];
  markets: Market[];
  mediaTypes: MediaType[];
  selectedNetworks: Network[];
  sites: Site[];
  targetedUnitIDs: number[] | BudgetBreakdownTargetedIDs;
  targetedSiteIDs: number[] | BudgetBreakdownTargetedIDs;
  units: Unit[];
  unitGeofencesToExport: GeofenceUnitModel[];
  siteGeofencesToExport: GeofenceSiteModel[];
  isAudienceInfoLoading: boolean;
  pageSizes;
  pageNumbers;
  lengths;
}

export class LocationTargetingData {
  public static deserialize(locationTargetingStr: string): ILocationTargetingData {
    if (locationTargetingStr == null) {
      return null;
    }

    const locationTargeting = JSON.parse(locationTargetingStr);

    return {
      ...locationTargeting,
      excludedLocations: locationTargeting.excludedLocations.map((location) => Location.fromApi(location)),
      excludedSites: locationTargeting.excludedSites.map((site) => Site.fromApi(site)),
      excludedUnits: locationTargeting.excludedUnits.map((unit) => Unit.fromApi(unit)),
      excludedMarkets: locationTargeting.excludedMarkets.map((market) => Market.fromApi(market)),
      excludedMediaTypes: locationTargeting.excludedMediaTypes.map((mediaType) => MediaType.fromApi(mediaType)),
      excludedNetworks: locationTargeting.excludedNetworks.map((networks) => Network.fromApi(networks)),
      excludedGeofenceGroups: locationTargeting?.excludedGeofenceGroups?.map((geofenceGroup) =>
        GeofenceGroup.fromApi(geofenceGroup),
      ),
      geofenceGroups: locationTargeting.geofenceGroups.map((geofenceGroup) => GeofenceGroup.fromApi(geofenceGroup)),
      locations: locationTargeting.locations.map((location) => Location.fromApi(location)),
      markets: locationTargeting.markets.map((market) => Market.fromApi(market)),
      mediaTypes: locationTargeting.mediaTypes.map((mediaType) => MediaType.fromApi(mediaType)),
      selectedNetworks: locationTargeting.selectedNetworks.map((networks) => Network.fromApi(networks)),
      sites: locationTargeting.sites.map((site) => Site.fromApi(site)),
      units: locationTargeting.units.map((unit) => Unit.fromApi(unit)),
    };
  }

  public static serialize(locationTargeting: ILocationTargetingData): string {
    if (locationTargeting == null) {
      return null;
    }
    return JSON.stringify({
      ...locationTargeting,
      excludedLocations: locationTargeting.excludedLocations.map((location) => location.toApi()),
      excludedSites: locationTargeting.excludedSites.map((site) => site.toApi()),
      excludedUnits: locationTargeting.excludedUnits.map((unit) => unit.toApi()),
      excludedMarkets: locationTargeting.excludedMarkets.map((market) => market.toApi()),
      excludedMediaTypes: locationTargeting.excludedMediaTypes.map((mediaType) => mediaType.toApi()),
      excludedNetworks: locationTargeting.excludedNetworks.map((networks) => networks.toApi()),
      excludedGeofenceGroups: locationTargeting.excludedGeofenceGroups.map((geofenceGroup) =>
        geofenceGroup.toApi(true),
      ),
      geofenceGroups: locationTargeting.geofenceGroups.map((geofenceGroup) => geofenceGroup.toApi(true)),
      locations: locationTargeting.locations.map((location) => location.toApi()),
      markets: locationTargeting.markets.map((market) => market.toApi()),
      mediaTypes: locationTargeting.mediaTypes.map((mediaType) => mediaType.toApi()),
      selectedNetworks: locationTargeting.selectedNetworks.map((networks) => networks.toApi()),
      sites: locationTargeting.sites.map((site) => site.toApi()),
      units: locationTargeting.units.map((unit) => unit.toApi()),
    });
  }
}
